import {
  hexToColor,
  infiniteWarCatalog,
} from "../../../data/infinite-war-catalog";
import type { RigTables } from "../../../view/unit-rig";
import type { AttackFxTables } from "../../../vfx/attack-fx";

export const FIELD = infiniteWarCatalog.units.field;
export const VIEW = infiniteWarCatalog.view;
export const RIG = VIEW.rig as unknown as RigTables;
export const ATTACK_FX = VIEW.attackFx as unknown as AttackFxTables;
export const WALK_ENTER = VIEW.layout.walkEnter;
export const ART_PACK = VIEW.artPack;
export const BOARD = VIEW.layout.board ?? { x: 26, y: 40, w: 488, h: 790 };
export const DEMIGOD_BAR = VIEW.layout.demigodBar ?? {
  h: 12,
  insetX: 18,
  northY: 48,
  southY: 804,
  labelGap: 4,
};

const HAND_DEFAULTS = {
  y: 912,
  pitch: 118,
  originX: 78,
  cardW: 112,
  cardH: 44,
  hitTop: 904,
  manaY: 838,
  hintY: 886,
  detailY: 852,
  detailH: 50,
} as const;

const handRaw = (VIEW.layout.hand ?? {}) as Partial<typeof HAND_DEFAULTS>;
export const HAND_UI = {
  y: handRaw.y ?? HAND_DEFAULTS.y,
  pitch: handRaw.pitch ?? HAND_DEFAULTS.pitch,
  originX: handRaw.originX ?? HAND_DEFAULTS.originX,
  cardW: handRaw.cardW ?? HAND_DEFAULTS.cardW,
  cardH: handRaw.cardH ?? HAND_DEFAULTS.cardH,
  hitTop: handRaw.hitTop ?? HAND_DEFAULTS.hitTop,
  manaY: handRaw.manaY ?? HAND_DEFAULTS.manaY,
  hintY: handRaw.hintY ?? HAND_DEFAULTS.hintY,
  detailY: handRaw.detailY ?? HAND_DEFAULTS.detailY,
  detailH: handRaw.detailH ?? HAND_DEFAULTS.detailH,
};
export const HAND_MAX = infiniteWarCatalog.units.mana.handMax;
export const HP_SHOW_MS = (VIEW.layout.hpBarShowSec ?? 1.6) * 1000;
export const HP_FADE_MS = (VIEW.layout.hpBarFadeSec ?? 0.4) * 1000;

/** Catalog sticky-tile art look (sprites + low-chroma tints). */
export const TILE_LOOK = VIEW.boardTiles ?? {
  neutralId: "tile_iw_neutral",
  celestialId: "tile_iw_celestial",
  deathId: "tile_iw_death",
  contestedId: "tile_iw_contested",
  neutralTint: "#1c2230",
  celestialTint: "#4a4638",
  deathTint: "#2e2a36",
  contestedTint: "#323038",
  alpha: 0.72,
  checkerAlphaMul: 0.9,
};

/**
 * Heaven (south) vs death (north) float / damage-number accents.
 * Shared by cards-hud, floating hit text, and spell VFX tint — do not fork.
 */
export const FACTION_FLOAT_CSS = {
  south: VIEW.colors.hpSouth as string,
  north: VIEW.colors.hpNorth as string,
} as const;

export type IwFactionColorKind = "veil" | "hp" | "float";

export function factionFloatCss(team: "south" | "north"): string {
  return team === "south" ? FACTION_FLOAT_CSS.south : FACTION_FLOAT_CSS.north;
}

export function factionFloatColor(team: "south" | "north"): number {
  return hexToColor(factionFloatCss(team));
}

/** Card / floater damage numbers — same SSOT as `factionFloat*` (hp palette). */
export function factionDamageColor(team: "south" | "north"): number {
  return factionFloatColor(team);
}

export function factionDamageColorCss(team: "south" | "north"): string {
  return factionFloatCss(team);
}

export function factionColor(team: "south" | "north", kind: IwFactionColorKind): number {
  if (kind === "hp" || kind === "float") {
    return factionFloatColor(team);
  }
  return hexToColor(team === "south" ? VIEW.colors.veilSouth : VIEW.colors.veilNorth);
}

export function blendColor(a: number, b: number, t: number): number {
  const clamp = Math.max(0, Math.min(1, t));
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * clamp);
  const g = Math.round(ag + (bg - ag) * clamp);
  const bl = Math.round(ab + (bb - ab) * clamp);
  return (r << 16) | (g << 8) | bl;
}
